import { GroupType } from '@ardoq/api-types';
import { GraphComponent, GraphItemTypes, INode, Point } from '@ardoq/yfiles';
import type { GraphNode } from '@ardoq/graph';
import { createSvgElement } from '@ardoq/dom-utils';
import {
  ModernizedHighlightIndicatorManager,
  defaultHandleImageDropCallback,
} from './modernized/ModernizedHighlightIndicatorManager';

const hitComponentId = (evt: DragEvent, graphComponent: GraphComponent) => {
  const location = graphComponent.viewToWorldCoordinates(
    new Point(evt.offsetX, evt.offsetY)
  );
  const {
    graphModelManager,
    renderTree: { contentGroup },
  } = graphComponent;
  const [hitElement] = graphModelManager.hitElementsAt({
    location,
    root: contentGroup,
    typeFilter: GraphItemTypes.NODE,
  });
  if (!hitElement) {
    return null;
  }
  const { tag } = hitElement;
  const { modelId } = tag as GraphNode;
  return modelId;
};

const isDraggingImage = (evt: DragEvent) => {
  const imageTypes = [
    'image/svg+xml',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/jpeg',
    'image/webp',
  ];
  if (evt.dataTransfer && evt.dataTransfer.items) {
    const fileType = evt.dataTransfer.items[0]?.type;
    if (fileType) return imageTypes.includes(fileType);
    return false;
  }
  return false;
};

const createMask = (clipId: string) => {
  const mask = createSvgElement('g', {
    'clip-path': `url(#${clipId})`,
  });
  const rect = createSvgElement('rect', {
    width: `100%`,
    height: `100%`,
    fill: 'white',
  });
  mask.appendChild(rect);
  return mask;
};

const configureInvalidDropzoneOverlayPanel = (
  overlayPanel: HTMLElement,
  clipId: string
) => {
  const invalidDropzonePattern = createSvgElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    opacity: '0.8',
    style:
      'position:absolute;top:0;left:0;height:100%;width:100%;display:block;pointer-events:none;',
  });
  const clipNode = createSvgElement('clipPath', { id: clipId });
  invalidDropzonePattern.appendChild(createMask(clipId));
  invalidDropzonePattern.appendChild(clipNode);
  overlayPanel.appendChild(invalidDropzonePattern);
  overlayPanel.setAttribute(
    'style',
    'position:absolute;top:0;left:0;height:100%;width:100%;display:block;pointer-events:none;'
  );

  return clipNode;
};

const initializeHighlightIndicator = (
  graphComponent: GraphComponent,
  viewInstanceId: string
) => {
  const modernizedHighlightManager: ModernizedHighlightIndicatorManager | null =
    graphComponent.highlightIndicatorManager instanceof
    ModernizedHighlightIndicatorManager
      ? graphComponent.highlightIndicatorManager
      : null;

  const addValidDropzoneHighlightToNodes = (evt: DragEvent) => {
    evt.preventDefault();

    transitioning = true;
    setTimeout(() => {
      transitioning = false;
    }, 1);

    if (isDraggingImage(evt)) {
      const { highlights } = graphComponent;
      for (const node of graphComponent.graph.nodes) {
        if (
          node.tag !== null &&
          node.tag?.type !== GroupType.COMPONENT &&
          node.tag?.type !== GroupType.FIELD &&
          node.tag?.type !== GroupType.TAG &&
          node.tag?.type !== GroupType.WORKSPACE
        ) {
          highlights.add(node);
          node.tag.isGroupNode = graphComponent.graph.isGroupNode(node);
        }
      }
    }
  };

  const setHoveredDropzone = (evt: DragEvent) => {
    evt.preventDefault();

    transitioning = true;
    setTimeout(() => {
      transitioning = false;
    }, 1);

    graphComponent.invalidate();

    const hoveredNodeId = hitComponentId(evt, graphComponent);

    graphComponent.graph.nodes.forEach((node: INode) => {
      if (
        !node.tag ||
        node.tag?.type === GroupType.COMPONENT ||
        node.tag?.type === GroupType.FIELD ||
        node.tag?.type === GroupType.TAG ||
        node.tag?.type === GroupType.WORKSPACE
      ) {
        return;
      }

      const nodeId = node.tag?.modelId || node.tag?.dataModel?.id || null;
      node.tag.isHovered = nodeId === hoveredNodeId;
    });
  };

  const clearHighlights = (evt: DragEvent) => {
    evt.preventDefault();
    graphComponent.invalidate();

    if (transitioning === false) {
      const { highlights } = graphComponent;
      highlights.clear();
      clipNode.innerHTML = '';
    }
  };

  const handleImageDropCallback = modernizedHighlightManager
    ? modernizedHighlightManager.handleImageDropCallback
    : defaultHandleImageDropCallback;

  const clipNode = configureInvalidDropzoneOverlayPanel(
    graphComponent.overlayPanel,
    `dropzone-clip-${viewInstanceId}`
  );

  let transitioning = false;

  if (handleImageDropCallback !== null) {
    const upsertIcon = (evt: DragEvent) => {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      graphComponent.invalidate();

      const componentId = hitComponentId(evt, graphComponent);
      const file = Array.from(evt.dataTransfer?.files || [])[0];

      if (componentId && file) {
        handleImageDropCallback(componentId, file);
      }
    };

    window.addEventListener('dragenter', addValidDropzoneHighlightToNodes);
    window.addEventListener('dragover', setHoveredDropzone);
    window.addEventListener('dragleave', clearHighlights);
    window.addEventListener('drop', clearHighlights);
    window.addEventListener('drop', upsertIcon);

    const unregister = () => {
      window.removeEventListener('dragenter', addValidDropzoneHighlightToNodes);
      window.removeEventListener('dragover', setHoveredDropzone);
      window.removeEventListener('dragleave', clearHighlights);
      window.removeEventListener('drop', clearHighlights);
      window.removeEventListener('drop', upsertIcon);
    };
    return unregister;
  }

  return () => {};
};
export { initializeHighlightIndicator };
